import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, RiskPill, PEPBadge, StatusPill, EmptyState, AtomBadge } from "../components/primitives";
import { formatNGN, maskBVN } from "../lib/format";
import { Search, X, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";

const RISK_OPTIONS = ["Low","Medium","High","Very High"];
const TIER_OPTIONS = ["Tier 1","Tier 2","Tier 3"];
const TYPE_OPTIONS = ["Individual","Sole proprietor","Limited company","Public company","Trust","Government/MDA"];
const SAVED_VIEWS = [
  {id:"all",label:"All customers",filters:{}},
  {id:"hr",label:"High-risk only",filters:{risk:"High,Very High"}},
  {id:"pep",label:"PEPs",filters:{pep:"yes"}},
  {id:"recent",label:"Onboarded last 30 days",filters:{}},
  {id:"sanctions",label:"Sanctions hits",filters:{}},
];

export default function Customers() {
  const [data, setData] = useState({ customers: [], total: 0 });
  const [filters, setFilters] = useState({ risk: "", tier: "", pep: "", type: "" });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("all");
  const [selected, setSelected] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "100" });
    if (filters.risk) params.set("risk", filters.risk);
    if (filters.tier) params.set("tier", filters.tier);
    if (filters.pep) params.set("pep", filters.pep);
    if (filters.type) params.set("type", filters.type);
    if (q) params.set("q", q);
    const r = await api.get(`/customers?${params}`);
    setData(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filters, q]);

  const toggle = (key, value) => {
    setFilters((f) => {
      const cur = (f[key] || "").split(",").filter(Boolean);
      const next = cur.includes(value) ? cur.filter(x => x !== value) : [...cur, value];
      return { ...f, [key]: next.join(",") };
    });
  };
  const isActive = (key, value) => (filters[key] || "").split(",").includes(value);

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${data.total.toLocaleString()} records · KYC tiers · risk-rated profiles · screening status`}
        sections={["5.2","5.3","5.4"]}
      />

      <div className="px-6 py-3 bg-bg-surface border-b border-line">
        {/* Saved views */}
        <div className="flex items-center gap-1 mb-3 overflow-x-auto">
          {SAVED_VIEWS.map(v => (
            <button key={v.id} data-testid={`saved-view-${v.id}`} onClick={() => { setView(v.id); setFilters({risk:v.filters.risk||"",tier:"",pep:v.filters.pep||"",type:""}); }}
              className={`px-2.5 py-1 text-[12px] rounded-sm border whitespace-nowrap ${view===v.id?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 px-2 py-1 border border-line rounded-sm bg-bg-surface flex-1 max-w-[360px]">
            <Search size={13} className="text-ink-subtle" />
            <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, BVN, NIN, account, phone…" className="bg-transparent text-[13px] outline-none flex-1" />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ink-subtle mr-1">Risk:</span>
            {RISK_OPTIONS.map(r => (
              <button key={r} data-testid={`filter-risk-${r.toLowerCase().replace(' ','-')}`} onClick={() => toggle("risk", r)} className={`px-2 py-0.5 text-[11px] rounded-sm border ${isActive("risk",r)?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>{r}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ink-subtle mr-1">Tier:</span>
            {TIER_OPTIONS.map(t => (
              <button key={t} onClick={() => toggle("tier", t)} className={`px-2 py-0.5 text-[11px] rounded-sm border ${isActive("tier",t)?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>{t}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ink-subtle mr-1">PEP:</span>
            {["yes","no"].map(p => (
              <button key={p} onClick={() => setFilters({...filters, pep: filters.pep === p ? "" : p})} className={`px-2 py-0.5 text-[11px] rounded-sm border ${filters.pep===p?'bg-amber text-navy border-amber':'border-line hover:bg-bg-hover'}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      <div className={`p-6 grid gap-3 ${selected ? "grid-cols-[minmax(0,1fr)_320px]" : "grid-cols-1"}`}>
        <Card className="min-w-0">
          {loading ? <div className="p-6 text-[13px] text-ink-muted">Loading…</div> : (
            <div className="overflow-x-auto">
              <table className="dense w-full tabular">
                <thead>
                  <tr>
                    <th className="w-6"></th>
                    <th>Customer</th>
                    <th>BVN</th>
                    <th>Type</th>
                    <th>KYC tier</th>
                    <th>Risk</th>
                    <th>PEP</th>
                    <th>Accounts</th>
                    <th className="text-right">Balance (NGN)</th>
                    <th>State</th>
                    <th>Branch</th>
                    <th>Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <>
                      <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => setSelected(c)} className={`cursor-pointer ${selected?.id === c.id ? "bg-amber/5" : ""}`}>
                        <td onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === c.id ? null : c.id); }}>
                          {expandedRow === c.id ? <ChevronDown size={13} strokeWidth={1.5} /> : <ChevronRight size={13} strokeWidth={1.5} className="text-ink-subtle hover:text-ink" />}
                        </td>
                        <td>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-[11px] text-ink-subtle">{c.id} · {c.occupation}</div>
                        </td>
                        <td className="text-[12px] hash-mono">{maskBVN(c.bvn)}</td>
                        <td className="text-[12px]">{c.type}</td>
                        <td><span className="text-[11px] px-1.5 py-0.5 border border-line rounded">{c.kyc_tier}</span></td>
                        <td><RiskPill band={c.risk_band} score={c.risk_score} /></td>
                        <td><PEPBadge pep={c.is_pep} /></td>
                        <td>{c.account_count}</td>
                        <td className="text-right">{formatNGN(c.total_balance, {short:true})}</td>
                        <td className="text-[12px]">{c.state}</td>
                        <td className="text-[12px]">{c.branch}</td>
                        <td className="text-[11px] text-ink-subtle">{c.opening_date}</td>
                      </tr>
                      {expandedRow === c.id && (
                        <tr className="bg-bg-warm">
                          <td colSpan={12} className="p-0">
                            <div className="p-4 grid grid-cols-4 gap-4 border-t-2 border-amber/30">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Identity</div>
                                <div className="text-[11px] space-y-0.5">
                                  <div><span className="text-ink-subtle">BVN:</span> <span className="hash-mono">{maskBVN(c.bvn)}</span></div>
                                  <div><span className="text-ink-subtle">NIN:</span> <span className="hash-mono">{c.nin ? maskBVN(c.nin) : "—"}</span></div>
                                  <div><span className="text-ink-subtle">Phone:</span> {c.phone || "—"}</div>
                                  <div><span className="text-ink-subtle">Email:</span> {c.email || "—"}</div>
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Risk signals</div>
                                <div className="text-[11px] space-y-0.5">
                                  <div><span className="text-ink-subtle">Risk score:</span> <span className="font-medium tabular">{c.risk_score}/100</span></div>
                                  <div><span className="text-ink-subtle">Risk band:</span> {c.risk_band}</div>
                                  <div><span className="text-ink-subtle">PEP:</span> {c.is_pep ? "Yes" : "No"}</div>
                                  <div><span className="text-ink-subtle">Sanctions hits:</span> {c.sanctions_hits ?? 0}</div>
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Accounts & activity</div>
                                <div className="text-[11px] space-y-0.5">
                                  <div><span className="text-ink-subtle">Accounts:</span> {c.account_count}</div>
                                  <div><span className="text-ink-subtle">Balance:</span> <span className="tabular">{formatNGN(c.total_balance, {short:true})}</span></div>
                                  <div><span className="text-ink-subtle">90d txn count:</span> {c.tx_count_90d ?? "—"}</div>
                                  <div><span className="text-ink-subtle">90d volume:</span> <span className="tabular">{formatNGN(c.tx_volume_90d || 0, {short:true})}</span></div>
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Quick actions</div>
                                <div className="flex flex-col gap-1.5">
                                  <Link to={`/customers/${c.id}`} className="px-2.5 py-1.5 text-[11px] bg-navy text-white rounded-md text-center hover:bg-navy-700">Open Customer 360 →</Link>
                                  <button className="px-2.5 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover">Re-run screening</button>
                                  <button className="px-2.5 py-1.5 text-[11px] border border-amber/40 bg-amber/8 text-amber-600 rounded-md hover:bg-amber/15">Request EDD</button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
              {data.customers.length === 0 && <EmptyState />}
            </div>
          )}
          <div className="px-3 py-2 border-t border-line flex items-center justify-between text-[11px] text-ink-subtle">
            <div>Showing {data.customers.length} of {data.total.toLocaleString()}</div>
            <div className="flex items-center gap-2">
              <button className="px-2 py-0.5 border border-line rounded-md hover:bg-bg-hover">Export CSV</button>
              <button className="px-2 py-0.5 border border-line rounded-md hover:bg-bg-hover">Export PDF</button>
            </div>
          </div>
        </Card>

        {/* Side panel preview */}
        {selected && (
          <Card>
            <div className="px-4 py-3 border-b border-line bg-gradient-to-r from-amber/8 to-white flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <RiskPill band={selected.risk_band} score={selected.risk_score} />
                  <PEPBadge pep={selected.is_pep} />
                </div>
                <div className="display text-[15px] font-semibold tracking-tight">{selected.name}</div>
                <div className="text-[11px] text-ink-subtle">{selected.id} · {selected.occupation}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-bg-hover rounded-md"><X size={14} strokeWidth={1.5} /></button>
            </div>
            <div className="p-3 text-[12px] space-y-1.5">
              <div className="flex justify-between"><span className="text-ink-subtle">Type</span><span>{selected.type}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">KYC tier</span><span>{selected.kyc_tier}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">BVN</span><span className="hash-mono">{maskBVN(selected.bvn)}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">State</span><span>{selected.state}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Branch</span><span>{selected.branch}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Onboarded</span><span>{selected.opening_date}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Source of funds</span><span className="text-right max-w-[60%]">{selected.source_of_funds || "—"}</span></div>
              <div className="flex justify-between pt-2 border-t border-line"><span className="text-ink-subtle">Total balance</span><span className="tabular font-medium">{formatNGN(selected.total_balance)}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Accounts</span><span>{selected.account_count}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">90d txn count</span><span className="tabular">{selected.tx_count_90d ?? "—"}</span></div>
            </div>
            <div className="px-3 pb-3 space-y-1.5">
              <Link to={`/customers/${selected.id}`} className="flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] bg-navy text-white rounded-lg hover:bg-navy-700">
                Open Customer 360 <ExternalLink size={12} strokeWidth={1.5} />
              </Link>
              <div className="grid grid-cols-2 gap-1.5">
                <button className="px-2 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover">Re-screen</button>
                <button className="px-2 py-1.5 text-[11px] border border-amber/40 bg-amber/8 text-amber-600 rounded-md hover:bg-amber/15">Request EDD</button>
              </div>
            </div>
            <div className="px-3 pb-3 pt-2 border-t border-line">
              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">Atom Neutron summary</div>
              <div className="text-[11px] text-ink-muted">Profile is consistent with declared occupation. Risk score within {selected.risk_band.toLowerCase()} band. Last screening cleared OFAC/UN/EU/CBN.</div>
              <div className="mt-2"><AtomBadge model="Atom Neutron" version="v2.0" /></div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
